import React, {useState, useMemo, useEffect} from 'react';
import './OutlookGrid.scss';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllEnterpriseModule } from 'ag-grid-enterprise';
import gridTheme from './GridTheme.js';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import countriesDictionary from '../../config/countriesDictionary.js';
import { UNITS_DICTIONARY, ACTIVITY_UNITS } from '../../config/unitsDictionary.js';
// Grid-specific data transform functions can be placed here or imported from a helper file

// data={data} countries={parsedRegions} selectedValues={selectedValues} products={parsedProducts} title={title}
const OutlookGrid = ({ data, countries, selectedValues, products, title = 'ESAI Outlook Data', onDataRendered }) => {

    const [gridData, setGridData] = useState([]);
    const [dynamicColumns, setDynamicColumns] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setGridData(sortByYearAndMonth(transformDataForGrid(data)));
        setLoading(false);
        if (onDataRendered) onDataRendered();
    }, [data]);

    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);

	useEffect(() => {
		let resizeTimeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				if (gridApi) {
					gridApi.autoSizeAllColumns();
				}
			}, 150);
		};
		window.addEventListener("resize", handleResize);
		return () => {
			clearTimeout(resizeTimeout);
			window.removeEventListener("resize", handleResize);
		};
	}, [gridApi]);

    useEffect(() => {

    }, [selectedValues]);
    const buildDynamicColumns = () => {
        const { products = [], activities = [], countries = [] } = selectedValues;
        
        if (!products.length || !activities.length || !countries.length) return [];

        // Parse region-country-subregion
        const regionCountryMap = {};
        const countrySubregionMap = {};
        countries.forEach(regionCountry => {
            const parts = regionCountry.split('.');
            if (parts.length === 3) {
                // REGION.COUNTRY.SUBREGION
                const [region, country, subregion] = parts;
                if (!regionCountryMap[region]) regionCountryMap[region] = [];
                if (!regionCountryMap[region].includes(country)) regionCountryMap[region].push(country);
                if (!countrySubregionMap[country]) countrySubregionMap[country] = [];
                if (!countrySubregionMap[country].includes(subregion)) countrySubregionMap[country].push(subregion);
            } else if (parts.length === 2) {
                const [region, country] = parts;
                if (!regionCountryMap[region]) regionCountryMap[region] = [];
                if (!regionCountryMap[region].includes(country)) regionCountryMap[region].push(country);
            }
        });

        const dynamicColumns = [];
        products.forEach(product => {
            const activityChildren = activities.map(activity => {
                // Build a flat array of columns for each region
                const regionColumns = [];
                Object.entries(regionCountryMap).forEach(([region, regionCountries]) => {
                    // For each country, add ALL REGIONS column if selected
                    regionCountries.forEach(country => {
                        const hasCountry = countries.some(sel => {
                            const parts = sel.split('.');
                            return parts.length === 2 && parts[1] === country;
                        });
                        if (hasCountry) {
                            regionColumns.push({
                                headerName: `${countriesDictionary[country]?.label || country}`,
                                field: `${product}-${activity}-${country}`,
                                sortable: true,
                                filter: true,
                                width: 120,
                                valueGetter: (params) => {
                                    const val = params.data[params.colDef.field]?.value;
                                    if (val === undefined || val === null) return val;
                                    const num = Number(val);
                                    if (isNaN(num)) return val;
                                    return num.toFixed(1);
                                },
                                cellClass: 'ag-right-aligned-cell',
                                cellClassRules: {
                                    'forecast-cell': (params) => params.data[params.colDef.field]?.status === 'FORECAST',
                                },
                            });
                        }
                        // For each subregion, add as a sibling column
                        const subregions = countrySubregionMap[country] || [];
                        subregions.forEach(subregion => {
                            regionColumns.push({
                                headerName: `${countriesDictionary[country]?.label || country} (${subregion})`,
                                field: `${product}-${activity}-${country}-${subregion}`,
                                sortable: true,
                                filter: true,
                                width: 120,
                                valueGetter: (params) => {
                                    const val = params.data[params.colDef.field]?.value;
                                    if (val === undefined || val === null) return val;
                                    const num = Number(val);
                                    if (isNaN(num)) return val;
                                    return num.toFixed(1);
                                },
                                cellClass: 'ag-right-aligned-cell',
                                cellClassRules: {
                                    'forecast-cell': (params) => params.data[params.colDef.field]?.status === 'FORECAST',
                                },
                            });
                        });
                    });
                });
                return {
                    headerName: activity + (ACTIVITY_UNITS[activity] ? ` (${UNITS_DICTIONARY[ACTIVITY_UNITS[activity]].shortLabel})` : ''),
                    children: regionColumns,
                };
            });
            dynamicColumns.push({
                headerName: product,
                children: activityChildren,
            });
        });
        return [
            {
                headerName: 'Date',
                children: [
                    { headerName: 'Year', field: 'year', sortable: true, filter: true, width: 80 },
                    {
                        headerName: 'Month',
                        field: 'month',
                        sortable: true,
                        filter: true,
                        width: 80,
                        valueFormatter: (params) => {
                            const monthNum = Number(params.value);
                            if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return params.value;
                            return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthNum - 1];
                        },
                        comparator: (valueA, valueB) => {
                            const numA = Number(valueA);
                            const numB = Number(valueB);
                            return numA - numB;
                        },
                    },
                ],
            },
            ...dynamicColumns,
        ];
    };

    useEffect(() => {
        const cols = buildDynamicColumns();
        setDynamicColumns(cols);
    }, [selectedValues]);

    const getContextMenuItems = (params) => {
        console.log('Context Menu Params:', params); // Debug log to inspect params
		const outlookTitle = params.context?.outlookTitle || 'ESAI Outlook Data';

		const dateStamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
		const baseFileName = `ESAI ${outlookTitle.replace(/\s+/g, '_')}_${dateStamp}`;
		const defaultItems = [
			'copy',
			'paste',
			'separator',
			{
				name: 'Export to CSV',
				action: () => {
					params.api.exportDataAsCsv({
						fileName: `${baseFileName}.csv`, // Set your custom filename here
					});
				},
			},
			{
				name: 'Export to Excel File',
				action: () => {
					params.api.exportDataAsExcel({
						fileName: `${baseFileName}.xlsx`, // Set your custom filename here
					});
				},
			},
		];
		return defaultItems;
	};

    const sortByYearAndMonth = (data, yearDir = 'asc', monthDir = 'asc') => {
            return [...data].sort((a, b) => {
                const yearMultiplier = yearDir === 'desc' ? -1 : 1;
                const monthMultiplier = monthDir === 'desc' ? -1 : 1;
                const yearDiff = yearMultiplier * (parseInt(a.year) - parseInt(b.year));
                if (yearDiff !== 0) return yearDiff;
                return monthMultiplier * (parseInt(a.month) - parseInt(b.month));
            });
        };

    // Helper to build key, omitting sector if blank/null/undefined
    function buildKey(item, isAllRegions) {
        const parts = [item.product];
        if (item.sector && item.sector.trim() !== "") parts.push(item.sector);
        parts.push(item.activity, item.country);
        if (!isAllRegions) parts.push(item.subregion);
        return parts.join("-");
    }

    const transformDataForGrid = (data) => {
        if (!Array.isArray(data)) return [];
        const grouped = {};
        data.forEach(item => {
            // Treat subregion 'NA', 'ALL', or blank/undefined as country-level (ALL SUBREGIONS)
            const isAllRegions = !item.subregion || item.subregion === '' || item.subregion === 'NA' || item.subregion === 'ALL' || item.subregion === 'ALL SUBREGIONS';
            const key = buildKey(item, isAllRegions);
            const idx = `${item.year}-${item.month}`;
            if (!grouped[idx]) {
                grouped[idx] = { year: item.year, month: item.month };
            }
            grouped[idx][key] = { value: item.value, status: item.status };
        });
        return Object.values(grouped);
    };

    return (
        <>
        <div className={`ag-theme-quartz outlook-grid`} style={{ height: '500px', width: '100%' }}>
            <AgGridReact
                theme={gridTheme}
                rowData={gridData}
                loading={loading}
                columnDefs={dynamicColumns}
                pagination={true}
                paginationPageSize={200}
                getContextMenuItems={getContextMenuItems}
                context={{ outlookTitle: title }}
                onGridReady={params => {
                    setGridApi(params.api);
                    setGridColumnApi(params.columnApi);
                    params.api.autoSizeAllColumns();
                }}
                onGridColumnsChanged={params => {
                    params.api.autoSizeAllColumns();
                }}
            />

        </div>
        </>
    )
};

export default OutlookGrid;
